import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, ArrowRight, X, Check, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { tierUnitPrice, gbp, makeMixboxItem, boostMult, boostOptions } from "@/lib/pricing";
import { useScrolling } from "@/lib/useScrolling";
import { useCart } from "@/context/CartContext";
import { BasketPanel } from "@/components/BasketPanel";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/PartnerContext";
import { groupFlavorsByRange } from "@/lib/flavorRanges";

const boostMultOf = (code) => boostMult(code);
const boostShort = (code) => (code === "standard" || !code ? "" : (code === "x2" ? "×2" : code === "x3" ? "×3" : ""));
const round2 = (n) => Math.round(n * 100) / 100;
// Admin "Quantities shown to customers": 1-10 exact, 11 = every quantity from 11 up.
const qtyAllowed = (size, n) => {
  const v = size?.visible_quantities;
  if (!Array.isArray(v)) return true;
  return v.map(Number).includes(n <= 10 ? n : 11);
};
// Snap `n` to the closest quantity the admin has enabled (dir: +1 search up first, -1 down first).
const nearestAllowedQty = (size, n, dir = 0) => {
  const want = Math.max(1, Math.min(999, n || 1));
  if (qtyAllowed(size, want)) return want;
  let up = null, down = null;
  for (let i = want + 1; i <= 999 && up === null; i++) if (qtyAllowed(size, i)) up = i;
  for (let i = want - 1; i >= 1 && down === null; i--) if (qtyAllowed(size, i)) down = i;
  if (dir > 0) return up ?? down ?? want;
  if (dir < 0) return down ?? up ?? want;
  if (up === null) return down ?? want;
  if (down === null) return up;
  return want - down <= up - want ? down : up;
};
// Smallest quantity a customer can order for this size (null when 1 is allowed → no message needed).
const minOrderQty = (size) => {
  if (qtyAllowed(size, 1)) return null;
  const v = (size?.visible_quantities || []).map(Number).sort((a, b) => a - b);
  return v.length ? v[0] : null;
};
const allowedQtyLabel = (size) => {
  const v = size?.visible_quantities;
  if (!Array.isArray(v) || v.length >= 11) return null;
  const sorted = v.map(Number).sort((a, b) => a - b);
  const contiguousToTop = sorted.every((q, i) => q === sorted[0] + i) && sorted[sorted.length - 1] === 11;
  if (contiguousToTop) return null; // just a minimum — the "minimum order" line covers it
  return v.map(Number).sort((a, b) => a - b).map((q) => (q === 11 ? "11+" : String(q))).join(", ");
};
// Disabled sizes (or sizes with no quantities enabled) stay visible as greyed "sold out" tiles.
const sizeSoldOut = (size) => size?.active === false || (Array.isArray(size?.visible_quantities) && size.visible_quantities.length === 0);
const visibleShopSizes = (allSizes, trade = false) => allSizes.filter((size) => {
  const ml = Number(size.ml || 0);
  // Trade (dynamic-pricing) accounts can order up to 10L online; 20L stays contact-only.
  return trade ? ml < 20000 : ml <= 5000;
});

export default function Shop({ eyebrow = "Build Your Box", title, subtitle, embedded = false, onProgress } = {}) {
  const brand = useBrand();
  title = title ?? `${brand.name} Syrup Concentrates`;
  const { user } = useAuth();
  const { items, addItem, bumpCart, pricingRules } = useCart();
  const BOOST_OPTS = useMemo(() => boostOptions(), [pricingRules]); // eslint-disable-line react-hooks/exhaustive-deps
  const BOOST_MAP = useMemo(() => Object.fromEntries(BOOST_OPTS.map((b) => [b.code, b])), [BOOST_OPTS]);
  const nav = useNavigate();
  const wholesale = !!(user && ["wholesale", "admin"].includes(user.role));
  const trade = user?.role === "dropship"; // dynamic-pricing account → show saving vs retail
  const scrolling = useScrolling();

  const [flavors, setFlavors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [bases, setBases] = useState([]);
  const [baseLabel, setBaseLabel] = useState("Base");

  const [sizeId, setSizeId] = useState(null);
  const [count, setCount] = useState(1);
  const [nextBase, setNextBase] = useState("CO");
  const [nextBoost, setNextBoost] = useState("standard");
  const [slots, setSlots] = useState([]); // [{ flavor, base, boost }]
  const [tiersOpen, setTiersOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/flavors"), api.get("/sizes", { params: { include_inactive: true } }), api.get("/bases"), api.get("/content"),
    ]).then(([f, s, b, c]) => {
      // Guest flavours are ordered only via the Guest Collection flow — never inside Build Your Box.
      setFlavors(groupFlavorsByRange(f.data).dreamlabs);
      const visibleSizes = visibleShopSizes(s.data, trade);
      setSizes(visibleSizes);
      setBases(b.data);
      setBaseLabel(c.data?.base_label || "Base");
      if (b.data[0]) setNextBase(b.data[0].code);
    });
  }, [wholesale, trade]);

  const size = useMemo(() => sizes.find((s) => s.id === sizeId), [sizes, sizeId]);
  const whPricing = wholesale && sizes.some((s) => (s.wholesale_tiers || []).length);
  const sortedTiers = useMemo(() => {
    const tiers = size ? (whPricing && size.wholesale_tiers ? size.wholesale_tiers : size.tiers) : [];
    return [...tiers].sort((a, b) => a.min_qty - b.min_qty);
  }, [size, whPricing]);
  const activeTierInfo = useMemo(() => {
    let idx = -1;
    sortedTiers.forEach((t, i) => { if (count >= t.min_qty) idx = i; });
    const cur = sortedTiers[idx];
    const next = sortedTiers[idx + 1] || null;
    let range = "";
    if (cur) {
      const upper = idx === sortedTiers.length - 1 ? null : sortedTiers[idx + 1].min_qty - 1;
      range = upper === null ? `${cur.min_qty}+` : (upper === cur.min_qty ? `${cur.min_qty}` : `${cur.min_qty}–${upper}`);
    }
    return { cur, next, range };
  }, [sortedTiers, count]);
  const activeMin = useMemo(() => {
    let m = null;
    sortedTiers.forEach((t) => { if (count >= t.min_qty) m = t.min_qty; });
    return m;
  }, [sortedTiers, count]);
  const [flashTier, setFlashTier] = useState(null);
  const prevActiveMin = useRef(null);
  useEffect(() => {
    if (activeMin !== null && prevActiveMin.current !== null && activeMin !== prevActiveMin.current) {
      setFlashTier(activeMin);
      const t = setTimeout(() => setFlashTier(null), 750);
      prevActiveMin.current = activeMin;
      return () => clearTimeout(t);
    }
    prevActiveMin.current = activeMin;
  }, [activeMin]);
  const filled = slots.filter(Boolean).length;
  useEffect(() => { onProgress?.(filled > 0); }, [filled, onProgress]);
  const baseUnit = size ? tierUnitPrice(sortedTiers, count) : 0; // standard per-bottle at this volume
  const boxTotal = useMemo(() => {
    let t = 0;
    for (let k = 0; k < count; k += 1) { const sl = slots[k]; t += baseUnit * (sl ? boostMultOf(sl.boost) : 1); }
    return round2(t);
  }, [slots, count, baseUnit]);
  const anyBoosted = slots.some((sl) => sl && sl.boost && sl.boost !== "standard");
  const remaining = count - filled;
  const complete = filled === count && count > 0;
  const buildStarted = filled > 0;

  const addBtnRef = useRef(null);
  const fillRef = useRef(null);
  const cartTargetRef = useRef(null);
  const [fly, setFly] = useState(null);
  const [pickerSlot, setPickerSlot] = useState(null);
  const [pickerBase, setPickerBase] = useState(null);
  const [pickerBoost, setPickerBoost] = useState("standard");
  const [sameAgainSlot, setSameAgainSlot] = useState(null);
  const [lowHint, setLowHint] = useState(null);
  const [completeModal, setCompleteModal] = useState(false);
  const wasComplete = useRef(false);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (complete && !wasComplete.current) {
      wasComplete.current = true;
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 1400);
      return () => clearTimeout(t);
    }
    if (!complete) { wasComplete.current = false; setCompleteModal(false); }
  }, [complete]);

  const setBottleCount = (n, dir = 0) => {
    const v = nearestAllowedQty(size, n, dir);
    setCount(v);
    setSlots((prev) => prev.slice(0, v));
    setPickerSlot(null);
    setSameAgainSlot(null);
  };

  const setSlotAt = (idx, f, base, boost) => {
    if (f.stock <= 0) { toast.error(`${f.name} is out of stock`); return; }
    setSlots((prev) => {
      const next = Array.from({ length: count }, (_, k) => prev[k] || null);
      next[idx] = { flavor: f, base, boost: boost || "standard" };
      return next;
    });
  };

  const openPicker = (i, base, boost) => { setSameAgainSlot(null); setPickerBase(base || nextBase); setPickerBoost(boost || nextBoost); setPickerSlot(i); };

  // Fill a tile from the picker; if empties remain, show the "Add same again" prompt on this tile.
  const chooseFlavour = (i, f, base, boost) => {
    if (f.stock <= 0) { toast.error(`${f.name} is out of stock`); return; }
    const arr = Array.from({ length: count }, (_, k) => slots[k] || null);
    arr[i] = { flavor: f, base, boost: boost || "standard" };
    setSlots(arr);
    setPickerSlot(null);
    setSameAgainSlot(arr.some((s) => !s) ? i : null);
    // Quietly suggest a well-stocked alternative if this flavour is running low.
    const thr = f.low_stock_threshold ?? 10;
    if (f.stock > 0 && f.stock <= thr) {
      const alt = flavors.filter((x) => x.id !== f.id && x.range_type !== "guest" && x.range_type !== "economy" && x.stock > (x.low_stock_threshold ?? 10)).sort((a, b) => b.stock - a.stock)[0];
      setLowHint(alt ? { slot: i, flavor: f, alt } : null);
    } else {
      setLowHint(null);
    }
  };

  const swapLowHint = () => {
    if (!lowHint) return;
    const cur = slots[lowHint.slot];
    setSlotAt(lowHint.slot, lowHint.alt, cur?.base || nextBase, cur?.boost || nextBoost);
    toast.success(`Swapped to ${lowHint.alt.name}`);
    setLowHint(null);
  };

  // Copy the prompted tile's selection into the next empty tile; move the prompt onto it.
  // Copy the prompted tile's selection into the next n empty tiles; move the prompt onto the last one filled.
  const addSameAgain = (n = 1) => {
    const src = slots[sameAgainSlot];
    if (!src) { setSameAgainSlot(null); return; }
    const arr = Array.from({ length: count }, (_, k) => slots[k] || null);
    let lastFilled = -1;
    for (let f = 0; f < n; f += 1) {
      const nextEmpty = arr.findIndex((s) => !s);
      if (nextEmpty === -1) break;
      arr[nextEmpty] = { ...src };
      lastFilled = nextEmpty;
    }
    setSlots(arr);
    setSameAgainSlot(lastFilled !== -1 && arr.some((s) => !s) ? lastFilled : null);
  };

  const fillRandom = () => {
    const avail = flavors.filter((f) => f.stock > 0);
    if (avail.length === 0) { toast.error("No flavours in stock right now"); return; }
    setSlots((prev) => {
      const next = Array.from({ length: count }, (_, k) => prev[k] || null);
      for (let k = 0; k < count; k += 1) {
        if (!next[k]) {
          const f = avail[Math.floor(Math.random() * avail.length)];
          next[k] = { flavor: f, base: nextBase, boost: nextBoost };
        }
      }
      return next;
    });
    setPickerSlot(null);
    toast.success(`Surprise ${baseLabel} mix ready! 🎲`);
  };

  const removeSlot = (idx) => setSlots((s) => { const next = [...s]; next[idx] = null; return next; });
  // Strengths an admin has enabled for a given base (Site Content → base strengths).
  const boostsForBase = (baseCode) => {
    const b = bases.find((x) => x.code === baseCode);
    const en = Array.isArray(b?.enabled_strengths) ? b.enabled_strengths : ["standard", "x2", "x3"];
    return BOOST_OPTS.filter((o) => en.includes(o.code));
  };
  const cycleSlotBase = (idx) => setSlots((s) => s.map((slot, i) => {
    if (i !== idx || !slot || bases.length < 2) return slot;
    const codes = bases.map((b) => b.code);
    return { ...slot, base: codes[(codes.indexOf(slot.base) + 1) % codes.length] };
  }));
  const cycleSlotBoost = (idx) => setSlots((s) => s.map((slot, i) => {
    if (i !== idx || !slot) return slot;
    const codes = boostsForBase(slot.base).map((b) => b.code);
    if (codes.length < 2) return slot;
    const cur = slot.boost || "standard";
    return { ...slot, boost: codes[(codes.indexOf(cur) + 1) % codes.length] };
  }));

  const changeSize = (id) => {
    const next = sizes.find((s) => s.id === id);
    if (sizeSoldOut(next)) { toast.message(`${next.label} is sold out — back soon`); return; }
    if (next?.id === sizeId) return;
    const v = nearestAllowedQty(next, 1, 1); // fresh size → 1 bottle, or that size's minimum
    if (v !== count) { setCount(v); setSlots((prev) => prev.slice(0, v)); }
    setSizeId(id); setPickerSlot(null); setSameAgainSlot(null); setLowHint(null);
  };

  // If an admin disables a strength for the selected base, drop it from the pickers.
  useEffect(() => {
    const ok = boostsForBase(nextBase).some((b) => b.code === nextBoost);
    if (!ok) setNextBoost("standard");
  }, [nextBase, bases]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pickerSlot === null) return;
    const ok = boostsForBase(pickerBase).some((b) => b.code === pickerBoost);
    if (!ok) setPickerBoost("standard");
  }, [pickerBase, pickerSlot, bases]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBoxToOrder = () => {
    if (!complete) { toast.error(`Fill all ${count} bottles first`); return false; }
    const filledSlots = slots.filter(Boolean);
    const lastColor = filledSlots[filledSlots.length - 1]?.flavor?.accent_color || "#A084E8";
    const s = addBtnRef.current?.getBoundingClientRect();
    const t = cartTargetRef.current?.getBoundingClientRect();
    if (s && t) setFly({ id: Date.now(), x0: s.left + s.width / 2 - 12, y0: s.top - 4, x1: t.left + t.width / 2 - 12, y1: t.top, color: lastColor });
    const counts = {};
    filledSlots.forEach(({ flavor, base, boost }) => {
      const bo = boost || "standard";
      const k = `${flavor.id}-${base}-${bo}`;
      counts[k] = counts[k] || { flavor_id: flavor.id, flavor_name: flavor.name, base, boost: bo, boost_label: BOOST_MAP[bo]?.label || "Standard", quantity: 0 };
      counts[k].quantity += 1;
    });
    const selections = Object.values(counts);
    addItem(makeMixboxItem(size, selections, { tiers: sortedTiers, isWholesale: wholesale }));
    toast.success(`Added ${count} × ${size?.label} to your order`);
    setSlots([]);
    setCount(1);
    setPickerSlot(null);
    setSameAgainSlot(null);
    setLowHint(null);
    setCompleteModal(false);
    return true;
  };
  const addAndCheckout = () => { if (addBoxToOrder()) nav("/checkout"); };

  const addMoreBottles = (n = 1) => { setCompleteModal(false); setBottleCount(count + n); };


  if (sizes.length === 0) return <div className="flex h-[60vh] items-center justify-center mono text-xs uppercase tracking-widest text-muted-foreground">Loading shop…</div>;

  return (
    <div className={`mx-auto max-w-[1300px] px-6 ${embedded ? "pt-2" : "pt-8"} pb-28 lg:pb-12`} data-testid="shop-page">
      {!embedded && (
        <>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">{eyebrow}</div>
          <h1 className="mt-1 font-heading text-3xl font-black tracking-tighter sm:text-4xl">{title}</h1>
          {subtitle && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
        </>
      )}
      {whPricing && (
        <div data-testid="wholesale-active-banner" className="mono mt-3 inline-block border border-primary bg-primary/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-primary">
          Wholesale pricing active
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          {/* ONE panel: size -> (opens in place) how many + defaults + bottle tiles */}
          <div className={`border bg-card p-4 transition-colors ${!size || complete ? "border-border" : "border-primary/50 ring-1 ring-primary/20"}`} data-testid="fill-panel" ref={fillRef}>
            {!size ? (
              <div data-testid="size-options">
                <div className="flex items-center gap-2.5">
                  <span className="mono flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground">1</span>
                  <span className="font-heading text-xl font-black tracking-tight">Choose your bottle size</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">One size per box. Pick a size and the box opens up right here — bottle count, flavours, base and strength all in one place.</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="size-selector">
                  {sizes.map((s) => {
                    const tiers = [...(whPricing && s.wholesale_tiers ? s.wholesale_tiers : s.tiers)].sort((a, b) => a.min_qty - b.min_qty);
                    const from = tierUnitPrice(tiers, 1);
                    const best = tiers.length ? Math.min(...tiers.map((t) => t.price)) : from;
                    const saving = from > 0 && best < from ? Math.round((1 - best / from) * 100) : 0;
                    const retailFrom = trade && Array.isArray(s.retail_tiers) && s.retail_tiers.length ? tierUnitPrice(s.retail_tiers, 1) : 0;
                    const vsRetail = retailFrom > from ? Math.round((1 - from / retailFrom) * 100) : 0;
                    const active = sizeId === s.id;
                    const soldOut = sizeSoldOut(s);
                    return (
                      <button key={s.id} data-testid={`size-${s.label}`} onClick={() => changeSize(s.id)} aria-disabled={soldOut}
                        className={`flex items-center justify-between gap-3 border px-4 py-3 text-left transition-colors ${soldOut ? "cursor-not-allowed border-border/50 opacity-50 grayscale" : active ? "border-primary bg-primary/10" : "border-border hover:border-primary"}`}>
                        <div className="min-w-0">
                          {soldOut ? (
                            <span data-testid={`size-sold-out-${s.label}`} className="mono mb-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Sold out · back soon</span>
                          ) : trade && vsRetail > 0 ? (
                            <span data-testid={`size-vs-retail-${s.label}`} className="mono mb-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-primary">save {vsRetail}% vs retail</span>
                          ) : (
                            <span className="mono mb-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-primary">{saving > 0 ? `up to ${saving}% off in bulk` : "one size box"}</span>
                          )}
                          <div className="font-heading text-xl font-black tracking-tighter">{s.label}</div>
                          <div className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{s.ml} ml · any flavour, base &amp; strength{!soldOut && minOrderQty(s) ? <span className="text-primary" data-testid={`size-min-${s.label}`}> · min {minOrderQty(s)} bottles</span> : null}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <div className="text-right leading-tight">
                            <div className="mono text-[10px] text-muted-foreground">per bottle</div>
                            <span className="mono text-sm font-bold text-primary">{gbp(from)}</span>
                            {trade && vsRetail > 0
                              ? <div className="mono text-[10px] text-muted-foreground line-through">{gbp(retailFrom)} retail</div>
                              : saving > 0 && <div className="mono text-[10px] text-muted-foreground">↓ {gbp(best)} bulk</div>}
                          </div>
                          {active && <Check className="h-4 w-4 text-primary" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {/* Row 1 — size chips (switch any time) */}
                <div className="flex flex-wrap items-center gap-2" data-testid="size-chip-row">
                  <span className="mono mr-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bottle size</span>
                  <div className="flex flex-wrap gap-1.5" data-testid="size-selector">
                    {sizes.map((s) => (
                      <button key={s.id} data-testid={`size-${s.label}`} onClick={() => changeSize(s.id)} aria-disabled={sizeSoldOut(s)} title={sizeSoldOut(s) ? "Sold out — back soon" : undefined}
                        className={`mono border px-3 py-1.5 text-[11px] font-bold transition-colors ${sizeSoldOut(s) ? "cursor-not-allowed border-border/50 line-through opacity-40" : sizeId === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 2 — how many + price, one line */}
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border pt-4" data-testid="count-panel">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">How many {size.label} bottles?</div>
                    <div className="mt-1.5 flex w-fit items-center border border-border">
                      <button data-testid="count-minus" onClick={() => setBottleCount(count - 1, -1)} className="p-2.5 hover:bg-muted"><Minus className="h-4 w-4" /></button>
                      <input data-testid="count-input" type="number" min="1" value={count} onChange={(e) => setBottleCount(parseInt(e.target.value))} className="w-16 border-x border-border bg-transparent py-2 text-center mono" />
                      <button data-testid="count-plus" onClick={() => setBottleCount(count + 1, 1)} className="p-2.5 hover:bg-muted"><Plus className="h-4 w-4" /></button>
                    </div>
                    {minOrderQty(size) && (
                      <div className="mono mt-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary" data-testid="qty-min-order">Minimum order for {size.label} is {minOrderQty(size)} bottles</div>
                    )}
                    {allowedQtyLabel(size) && (
                      <div className="mono mt-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground" data-testid="qty-allowed-hint">Available in: {allowedQtyLabel(size)}</div>
                    )}
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Box total</div>
                    <div className="mono mt-1 font-heading text-2xl font-black leading-none text-primary" data-testid="section2-box-total">{gbp(boxTotal)}</div>
                    <div className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{count} × {size.label}{anyBoosted ? " · incl. strength" : ""}</div>
                  </div>
                  <div className="min-w-[260px] flex-1">
                <div data-testid="volume-pricing">
                    <button onClick={() => setTiersOpen((o) => !o)} data-testid="tiers-toggle"
                      className="group flex w-full items-center justify-between gap-3 border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary">
                      <div className="min-w-0">
                        <div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{whPricing ? "Wholesale pricing" : "Volume pricing"} · {size.label}</div>
                        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="mono text-base font-black text-primary" data-testid="tiers-current-price">{gbp(baseUnit)}</span>
                          <span className="mono text-[11px] text-muted-foreground">per bottle{activeTierInfo.range ? ` · qty ${activeTierInfo.range}` : ""}</span>
                          {activeTierInfo.next && (
                            <span className="mono text-[10px] text-primary" data-testid="tiers-next-nudge">↓ {gbp(activeTierInfo.next.price)} at {activeTierInfo.next.min_qty}+</span>
                          )}
                        </div>
                      </div>
                      <span className="mono flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-primary">
                        {tiersOpen ? "Hide" : "All tiers"}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${tiersOpen ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {tiersOpen && (
                        <motion.div key="tier-grid" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }} className="overflow-hidden">
                          <div className="mono mb-2 mt-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground"><span className="text-primary">Tap a tier</span> to set your quantity</div>
                          <div className="grid grid-cols-4 gap-px overflow-hidden border border-border bg-border sm:grid-cols-6 lg:grid-cols-7" data-testid="tier-table">
                            {sortedTiers.map((t, i, arr) => {
                              const active = count >= t.min_qty && (i === arr.length - 1 || count < arr[i + 1].min_qty);
                              const upper = i === arr.length - 1 ? null : arr[i + 1].min_qty - 1;
                              const tierMax = upper === null ? Math.max(t.min_qty, 11) : upper;
                              let purchasable = false;
                              for (let q = t.min_qty; q <= tierMax && !purchasable; q++) purchasable = qtyAllowed(size, q);
                              if (!purchasable) return null;
                              const range = upper === null ? `${t.min_qty}+` : (upper === t.min_qty ? `${t.min_qty}` : `${t.min_qty}–${upper}`);
                              return (
                                <motion.button key={t.min_qty} type="button" onClick={() => setBottleCount(t.min_qty, 1)}
                                  title={`Set order to ${t.min_qty} bottle${t.min_qty === 1 ? "" : "s"}`}
                                  animate={flashTier === t.min_qty ? { scale: [1, 1.14, 1] } : { scale: 1 }}
                                  transition={{ duration: 0.55 }}
                                  className={`relative p-2 text-center transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`} data-testid={`tier-${t.min_qty}`}>
                                  <div className="mono text-[10px] uppercase tracking-[0.1em] opacity-70">{range}</div>
                                  <div className="mono mt-1 text-sm font-bold">{gbp(t.price)}</div>
                                </motion.button>
                              );
                            })}
                          </div>
                          <div className="mono mt-2 text-[11px] text-muted-foreground" data-testid="tier-hint">Buy more of a size and the price per bottle drops — fewer, bigger bottles are always better value.</div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  </div>
                </div>

                {/* Row 3 — defaults + bottle tiles */}
                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <div className="flex items-center gap-2.5">
                      <span className="font-heading text-lg font-black tracking-tight">Fill each bottle</span>
                      <span className="mono text-[11px] text-muted-foreground" data-testid="fill-progress">{filled}/{count} filled</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{baseLabel}</span>
                        <div className="flex gap-1" data-testid="next-base-selector">
                          {bases.map((b) => (
                            <button key={b.code} data-testid={`next-base-${b.code}`} onClick={() => setNextBase(b.code)} title={b.description || undefined}
                              className={`border px-3 py-1 text-xs font-medium transition-colors ${nextBase === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{b.name || b.code}</button>
                          ))}
                        </div>
                      </div>
                      {bases.find((b) => b.code === nextBase)?.description && (
                        <p className="max-w-md text-[11px] leading-snug text-muted-foreground" data-testid="next-base-description">{bases.find((b) => b.code === nextBase).description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Strength</span>
                      <div className="flex gap-1" data-testid="next-boost-selector">
                        {boostsForBase(nextBase).map((b) => (
                          <button key={b.code} data-testid={`next-boost-${b.code}`} onClick={() => setNextBoost(b.code)} title={b.note ? `${b.label} (${b.note})` : b.label}
                            className={`flex items-center gap-1.5 border px-2.5 py-1 text-xs font-medium transition-colors ${nextBoost === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                            <span title={b.note}>{b.short}</span>
                            <span className="mono text-[9px] opacity-80">{`+${gbp(b.mult === 1 ? 0 : round2(baseUnit * (b.mult - 1)))}`}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="fill-instructions">Click a bottle to choose its flavour — base and strength can be changed per bottle inside the picker.</p>
                  <AnimatePresence>
                    {lowHint && (
                      <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="mt-2 flex flex-wrap items-center gap-2 border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-xs" data-testid="low-stock-hint">
                        <span className="text-yellow-700 dark:text-yellow-500">Heads up — <b>{lowHint.flavor.name}</b> is running low ({lowHint.flavor.stock} left). Prefer a fully-stocked pick?</span>
                        <button type="button" onClick={swapLowHint} data-testid="low-stock-hint-swap"
                          className="mono border border-primary bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground transition-transform hover:-translate-y-0.5">
                          Use {lowHint.alt.name}
                        </button>
                        <button type="button" onClick={() => setLowHint(null)} data-testid="low-stock-hint-dismiss" className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-3">
                    {pickerSlot !== null && (
                      <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setPickerSlot(null)} data-testid="picker-backdrop" />
                    )}
                <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8" data-testid="slot-grid">
              {Array.from({ length: count }).map((_, i) => {
                const slot = slots[i];
                const open = pickerSlot === i;
                return (
                  <div key={i} className="relative">
                    {slot ? (
                      <div className="relative flex aspect-square cursor-pointer flex-col overflow-hidden border" style={{ borderColor: slot.flavor.accent_color }}
                        onClick={() => openPicker(i, slot.base, slot.boost)} data-testid={`slot-tile-${i}`}>
                        {sameAgainSlot === i && (
                          <div data-testid={`same-again-${i}`} onClick={(e) => e.stopPropagation()}
                            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 bg-primary/60 text-primary-foreground backdrop-blur-[2px]">
                            <span className="mono text-center text-[8px] font-bold uppercase leading-tight tracking-widest">Same again</span>
                            <div className="flex flex-wrap justify-center gap-1">
                              <button type="button" onClick={(e) => { e.stopPropagation(); addSameAgain(1); }} data-testid={`same-again-1-${i}`}
                                className="mono rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-black text-primary transition-colors hover:bg-white">+1</button>
                              {remaining > 1 && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); addSameAgain(5); }} data-testid={`same-again-5-${i}`}
                                  className="mono rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-black text-primary transition-colors hover:bg-white">+5</button>
                              )}
                              {remaining > 1 && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); addSameAgain(remaining); }} data-testid={`same-again-all-${i}`}
                                  className="mono rounded bg-white px-1.5 py-0.5 text-[10px] font-black text-primary ring-1 ring-primary transition-colors hover:bg-primary hover:text-primary-foreground">+All</button>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="relative min-h-0 flex-1 bg-white">
                          {slot.flavor.image_url ? (
                            <img src={slot.flavor.image_url} alt={slot.flavor.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full" style={{ background: slot.flavor.accent_color }} />
                          )}
                          {slot.boost && slot.boost !== "standard" && (
                            <button onClick={(e) => { e.stopPropagation(); cycleSlotBoost(i); }} data-testid={`slot-boost-${i}`}
                              className="mono absolute left-1 top-1 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary-foreground shadow">{boostShort(slot.boost)}</button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); removeSlot(i); setPickerSlot(null); }} data-testid={`remove-slot-${i}`} className="absolute right-1 top-1 rounded-full bg-black/50 p-0.5 text-white hover:bg-black/80"><X className="h-3 w-3" /></button>
                        </div>
                        <div className="flex items-center justify-between gap-1 px-1.5 py-1" style={{ background: slot.flavor.accent_color }}>
                          <span className="truncate text-[9px] font-bold leading-tight text-white drop-shadow">{slot.flavor.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); cycleSlotBase(i); }} data-testid={`slot-base-${i}`} className="mono shrink-0 rounded-full bg-black/40 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white hover:bg-black/70">{slot.base}</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => openPicker(i, nextBase, nextBoost)} data-testid={`slot-tile-${i}`}
                        className={`relative flex aspect-square w-full flex-col items-center justify-center gap-1 overflow-hidden border border-dashed p-1 text-center transition-colors hover:border-primary hover:bg-muted ${open ? "border-primary bg-muted" : "choose-flavour-pulse border-border"}`}>
                        {!open && <span className="choose-flavour-sheen" aria-hidden="true" />}
                        <Plus className="h-4 w-4 text-primary" />
                        <span className="mono text-[9px] font-bold uppercase leading-tight tracking-[0.1em] text-primary">Choose flavour</span>
                      </button>
                    )}
                    {open && (
                      <div className="fixed left-1/2 top-1/2 z-50 w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 border border-border bg-card p-3 text-left shadow-2xl shadow-black/60" data-testid={`slot-picker-${i}`} onClick={(e) => e.stopPropagation()}>
                        <div className="mono mb-1.5 flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                          <span>Bottle {i + 1} · {baseLabel}</span>
                          <button onClick={() => setPickerSlot(null)} data-testid={`picker-close-${i}`} className="rounded-full p-0.5 hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
                        </div>
                        <div className="mb-1 flex gap-1">
                          {bases.map((b) => (
                            <button key={b.code} onClick={() => setPickerBase(b.code)} data-testid={`picker-base-${i}-${b.code}`} title={b.description || undefined}
                              className={`flex-1 border px-2 py-1.5 text-xs font-medium transition-colors ${pickerBase === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{b.name || b.code}</button>
                          ))}
                        </div>
                        {bases.find((b) => b.code === pickerBase)?.description && (
                          <p className="mb-2 text-[10px] leading-snug text-muted-foreground" data-testid={`picker-base-description-${i}`}>{bases.find((b) => b.code === pickerBase).description}</p>
                        )}
                        <div className="mb-2" />
                        <div className="mono mb-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Strength</div>
                        <div className="mb-3 flex gap-1" data-testid={`picker-boost-row-${i}`}>
                          {boostsForBase(pickerBase).map((b) => (
                            <button key={b.code} onClick={() => setPickerBoost(b.code)} data-testid={`picker-boost-${i}-${b.code}`}
                              className={`flex flex-1 flex-col items-center border px-1 py-1.5 text-xs font-medium transition-colors ${pickerBoost === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                              <span title={b.note}>{b.short}</span>
                              <span className="mono text-[10px] opacity-80">{`+${gbp(b.mult === 1 ? 0 : round2(baseUnit * (b.mult - 1)))}`}</span>
                            </button>
                          ))}
                        </div>
                        <div className="mono mb-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Flavour <span className="opacity-60">· {flavors.length} to choose from</span></div>
                        <div className="grid grid-cols-5 gap-1.5" data-testid={`picker-flavor-grid-${i}`}>
                          {flavors.map((f) => (
                            <button key={f.id} disabled={f.stock <= 0} data-testid={`picker-flavor-${i}-${f.id}`}
                              onClick={() => chooseFlavour(i, f, pickerBase, pickerBoost)}
                              className="flex flex-col items-center gap-1 border border-border p-1.5 text-center transition-colors hover:border-primary hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 disabled:grayscale">
                              <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-border bg-white">
                                {f.image_url ? <img src={f.image_url} alt="" className="h-full w-full object-cover" /> : <span className="block h-full w-full rounded-full" style={{ background: f.accent_color }} />}
                              </span>
                              <span className="text-[9px] font-medium leading-tight">{f.name}</span>
                              {f.stock <= 0 && <span className="mono text-[8px] font-bold uppercase tracking-[0.1em] text-destructive" data-testid={`flavor-sold-out-${f.id}`}>Sold out</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit" data-testid="shop-summary">
          <div className="border border-border bg-card p-5">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Your Box</div>
            {!size ? (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="shop-summary-empty">Choose a bottle size to begin.</p>
            ) : (
            <>
            <div className="mt-2 font-heading text-2xl font-black tracking-tighter" data-testid="shop-heading">{count} × {size.label}</div>
            <div className="mono mt-1 text-[11px] text-muted-foreground" data-testid="shop-fill-status">{complete ? "All bottles flavoured ✓" : `${filled}/${count} flavoured`}</div>
            <div className="mt-4 border-t border-border pt-4">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">{gbp(baseUnit)} × {count} bottle{count === 1 ? "" : "s"}</span><span className="mono font-bold">{gbp(round2(baseUnit * count))}</span></div>
              {anyBoosted && <div className="mt-1.5 flex justify-between text-sm"><span className="text-muted-foreground">Strength boosts</span><span className="mono font-bold text-primary">+{gbp(round2(boxTotal - baseUnit * count))}</span></div>}
              <div className="mt-3 flex items-end justify-between border-t border-border pt-3">
                <span className="text-sm font-semibold">Total</span>
                <span className="font-heading text-3xl font-black text-primary mono" data-testid="shop-total">{gbp(boxTotal)}</span>
              </div>
              <div className="mono mt-1 text-[10px] text-muted-foreground">before shipping · crypto only</div>
            </div>
            <AnimatePresence>
              {complete && (
                <motion.div key="ci" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="box-complete-inline" className="mt-4 flex items-center gap-2 border border-primary bg-primary/15 px-3 py-2 text-sm font-semibold text-primary">
                  <Check className="h-4 w-4" /> Box complete — ready to add
                </motion.div>
              )}
            </AnimatePresence>
            {complete ? (
              <>
                <motion.button ref={addBtnRef} data-testid="add-and-checkout-btn" onClick={addAndCheckout}
                  animate={pulse ? { scale: [1, 1.04, 1] } : { scale: 1 }} transition={{ duration: 0.6, repeat: pulse ? 1 : 0 }}
                  className="mt-4 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                  Checkout with this box — {gbp(boxTotal)} <ArrowRight className="h-4 w-4" />
                </motion.button>
                <button data-testid="add-box-btn" onClick={addBoxToOrder}
                  className="mono mt-2 flex w-full items-center justify-center gap-2 border border-border py-3 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary">
                  <Plus className="h-3.5 w-3.5" /> Add box &amp; build another
                </button>
              </>
            ) : (
              <>
                <button ref={addBtnRef} data-testid="add-box-btn" disabled
                  className="mt-4 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground opacity-40">
                  <Plus className="h-4 w-4" /> Add to Order
                </button>
                <p className="mono mt-2 text-center text-[11px] text-muted-foreground" data-testid="shop-blocked">{remaining} more bottle{remaining === 1 ? "" : "s"} to fill before you can add</p>
              </>
            )}
            </>
            )}
            <div ref={cartTargetRef}><BasketPanel /></div>
          </div>
        </aside>
      </div>

      {/* Box complete — blocking prompt */}
      <AnimatePresence>
        {completeModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="box-complete-modal-backdrop">
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 6 }} transition={{ duration: 0.25 }}
              className="w-[min(26rem,calc(100vw-2rem))] border border-primary bg-card p-6 shadow-2xl shadow-black/60" data-testid="box-complete-modal">
              <div className="flex items-center gap-2 text-primary">
                <Check className="h-5 w-5" />
                <span className="mono text-[11px] uppercase tracking-[0.2em]">Box complete</span>
              </div>
              <div className="mt-2 font-heading text-2xl font-black tracking-tight">Your {size?.label} box is full</div>
              <div className="mono mt-1 text-sm text-muted-foreground">{count} × {size?.label} · <span className="font-bold text-primary">{gbp(boxTotal)}</span></div>
              <button onClick={addBoxToOrder} data-testid="modal-add-box"
                className="mt-5 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                <Plus className="h-4 w-4" /> Add Box to Order — {gbp(boxTotal)}
              </button>
              <button onClick={() => setCompleteModal(false)} data-testid="modal-keep-editing"
                className="mono mt-3 w-full text-center text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary">
                Keep editing bottles
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Flying bottle feedback */}
      <AnimatePresence>
        {fly && (
          <motion.div key={fly.id}
            initial={{ x: fly.x0, y: fly.y0, opacity: 1, scale: 1 }}
            animate={{ x: fly.x1, y: fly.y1, opacity: [1, 1, 0.2], scale: [1, 1.15, 0.35] }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            onAnimationComplete={() => { bumpCart(); setFly(null); }}
            style={{ position: "fixed", left: 0, top: 0, zIndex: 100, pointerEvents: "none" }}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white shadow-xl" style={{ background: fly.color }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky mobile action bar (hidden on desktop — desktop uses the pinned summary); only once a size is chosen */}
      {size && (
      <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-xl transition-transform duration-300 lg:hidden ${scrolling ? "translate-y-full" : "translate-y-0"}`} data-testid="mobile-action-bar">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
              {complete ? "Box ready" : `${filled}/${count} filled · ${remaining} to go`}
            </div>
            <div className="font-heading text-2xl font-black leading-none text-primary mono" data-testid="mobile-box-total">{gbp(boxTotal)}</div>
          </div>
          {items.length > 0 && (
            <button onClick={() => nav("/cart")} data-testid="mobile-view-cart"
              className="relative flex shrink-0 items-center gap-1.5 border border-border px-3 py-2.5 text-xs font-medium">
              <ShoppingBag className="h-4 w-4" />
              <span className="mono">{items.reduce((s, i) => s + i.quantity, 0)}</span>
            </button>
          )}
          <button onClick={addBoxToOrder} disabled={!complete} data-testid="mobile-add-box"
            className="flex shrink-0 items-center gap-2 bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40">
            {complete ? <><Plus className="h-4 w-4" /> Add to Order</> : `${remaining} more`}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
